import React, { useState, useEffect, useMemo } from "react";
import { Tag, Button, Typography, Space, Badge, Card } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import TasksDetails from "./TasksDetails";
import DrawerComponent from "../../../components/Forms/Drawer";
import ProfileCard from "../../../components/ContactInfo/ProfileCard";
import { constants } from "../../../constants/common";
import SmallProfileCard from "../../../components/ContactInfo/SmallProfileCard";
import ReusableTable from "../../../components/TableComponent";
import { MasterConfigs } from "../../../interfaces/entity";
import { getData } from "../../../actions/apiCall";

const { Title, Text } = Typography;

interface Task {
    _id: string;
    subject: string;
    description: string;
    dueDate: string;
    taskStatus: string;
    priority: string;
    refType: string;
    refId: any;
    taskOwner: any;
    createdAt: string;
}

const BDETasks: React.FC = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state: any) => state.app);
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [openDrawer, setOpenDrawer] = useState<boolean>(false);
    const [filter, setFilter] = useState<'all' | 'pending' | 'overdue' | 'interview'>('all');
    const [allTasks, setAllTasks] = useState<Task[]>([]);
    const [refreshKey, setRefreshKey] = useState(0);

    // Fetch all tasks to calculate counts
    useEffect(() => {
        const fetchAllTasks = async () => {
            try {
                const response: any = await dispatch(getData('/tasks-management'));
                if (response?.code === 200 && response?.data) {
                    setAllTasks(response.data);
                }
            } catch (error) {
                console.error('Error fetching tasks:', error);
            }
        };
        fetchAllTasks();
    }, [refreshKey]);

    // Calculate filter counts
    const pendingTasksCount = allTasks.filter(t =>
        t.taskStatus !== 'Completed' && t.taskStatus !== 'Cancelled'
    ).length;

    const overdueTasksCount = allTasks.filter(t =>
        moment(t.dueDate).isBefore(moment(), 'day') &&
        t.taskStatus !== 'Completed' && t.taskStatus !== 'Cancelled'
    ).length;

    const interviewTasksCount = allTasks.filter(t => t.refType === 'ContractInterview').length;

    // Get query params based on filter - memoized to ensure reference changes
    // Note: API supports simple key-value filters, complex filters handled client-side
    const queryParams = useMemo(() => {
        const params: any = {};

        if (filter === 'interview') {
            params.refType = 'ContractInterview';
        }
        // For pending and overdue, we'll filter client-side after fetching
        // since API doesn't support $nin or $lt operators directly

        return params;
    }, [filter]);

    const getDueStatus = (dueDate: string) => {
        const due = moment(dueDate);
        const now = moment();

        if (due.isBefore(now, 'day')) {
            return { status: 'Overdue', color: 'red' };
        } else if (due.isSame(now, 'day')) {
            return { status: 'Due Today', color: 'orange' };
        } else if (due.isSame(now.clone().add(1, 'day'), 'day')) {
            return { status: 'Due Tomorrow', color: 'blue' };
        } else {
            return { status: 'Upcoming', color: 'green' };
        }
    };

    const getPriorityColor = (priority: string) => {
        const colors: { [key: string]: string } = {
            'Highest': 'red',
            'High': 'orange',
            'Normal': 'blue',
            'Low': 'green',
            'Lowest': 'default'
        };
        return colors[priority] || 'default';
    };

    const handleTaskClick = (task: Task) => {
        setSelectedTask(task);
        setOpenDrawer(true);
    };

    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
    };

    // Transform response to apply client-side filters for pending and overdue
    const transformResponse = useMemo(() => {
        return (response: any) => {
            let data = response?.data || [];
            const originalTotal = response?.pagination?.total || data.length;

            if (filter === 'pending') {
                data = data.filter((task: Task) =>
                    task.taskStatus !== 'Completed' && task.taskStatus !== 'Cancelled'
                );
            } else if (filter === 'overdue') {
                data = data.filter((task: Task) =>
                    moment(task.dueDate).isBefore(moment(), 'day') &&
                    task.taskStatus !== 'Completed' && task.taskStatus !== 'Cancelled'
                );
            }

            // For client-side filtered results, return filtered count
            // For server-side filtered (interview), return original pagination
            return {
                data,
                total: (filter === 'pending' || filter === 'overdue') ? data.length : originalTotal
            };
        };
    }, [filter]);

    const config: MasterConfigs = useMemo(() => ({
        api: () => `/tasks-management`,
        queryParams: queryParams,
        loadPagination: true,
        getColumns: () => [
            {
                title: "Subject",
                dataIndex: "subject",
                key: "subject",
                render: (text: string, record: Task) => (
                    <Space>
                        {record.refType === 'ContractInterview' && (
                            <Tag color="blue" icon={<FileTextOutlined />}>
                                Interview Feedback
                            </Tag>
                        )}
                        <a
                            onClick={() => handleTaskClick(record)}
                            style={{ fontWeight: 500 }}
                        >
                            {text}
                        </a>
                    </Space>
                ),
                width: 300,
            },
            {
                title: "Due Date",
                dataIndex: "dueDate",
                key: "dueDate",
                render: (text: string) => {
                    const dueStatus = getDueStatus(text);
                    return (
                        <Space>
                            <span>{moment(text).format('DD MMM YYYY')}</span>
                            <Tag color={dueStatus.color} bordered={false}>
                                {dueStatus.status}
                            </Tag>
                        </Space>
                    );
                },
                width: 200,
            },
            {
                title: "Task Status",
                dataIndex: "taskStatus",
                key: "taskStatus",
                width: 150,
            },
            {
                title: "Priority",
                dataIndex: "priority",
                key: "priority",
                render: (text: string) => (
                    <Tag color={getPriorityColor(text)}>
                        {text}
                    </Tag>
                ),
                width: 120,
            },
            {
                title: "Related To",
                dataIndex: "refId",
                key: "refId",
                render: (text: any, record: Task) => {
                    if (['Leads', 'Contacts', 'User', 'ShortlistedCandidatesForInterview'].includes(record.refType)) {
                        return (
                            <SmallProfileCard
                                name={constants.GET_FULL_NAME(text)}
                                avatar={text?.profilePhoto || text?.avatar}
                            />
                        );
                    } else if (record.refType === 'Businesses') {
                        return text?.businessName || 'N/A';
                    } else if (record.refType === 'Deals') {
                        return text?.dealName || 'N/A';
                    } else if (record.refType === 'ContractInterview') {
                        return text?.interviewTitle || text?.name || 'Interview';
                    }
                    return 'N/A';
                },
                width: 200,
            },
            {
                title: "Description",
                dataIndex: "description",
                key: "description",
                render: (text: string) => (
                    <Text type="secondary" ellipsis style={{ maxWidth: 300 }}>
                        {text || 'N/A'}
                    </Text>
                ),
                width: 250,
            },
            {
                title: "Created At",
                dataIndex: "createdAt",
                key: "createdAt",
                render: (text: string) => moment(text).format('DD MMM YYYY'),
                width: 150,
            },
        ],
        renderComponent: () => (
            <Card style={{ marginBottom: '16px' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                        <Title level={4} style={{ margin: 0 }}>My Tasks</Title>
                        <Text type="secondary">View and manage your assigned tasks</Text>
                    </div>

                    {/* Filter Buttons */}
                    <Space size="middle" wrap>
                        <Button
                            type={filter === 'all' ? 'primary' : 'default'}
                            onClick={() => setFilter('all')}
                        >
                            All Tasks ({allTasks.length})
                        </Button>
                        <Badge count={pendingTasksCount} offset={[10, 0]}>
                            <Button
                                type={filter === 'pending' ? 'primary' : 'default'}
                                onClick={() => setFilter('pending')}
                            >
                                Pending
                            </Button>
                        </Badge>
                        <Badge count={overdueTasksCount} offset={[10, 0]}>
                            <Button
                                type={filter === 'overdue' ? 'primary' : 'default'}
                                onClick={() => setFilter('overdue')}
                                danger
                            >
                                Overdue
                            </Button>
                        </Badge>
                        <Badge count={interviewTasksCount} offset={[10, 0]}>
                            <Button
                                type={filter === 'interview' ? 'primary' : 'default'}
                                onClick={() => setFilter('interview')}
                            >
                                Interview Feedback
                            </Button>
                        </Badge>
                    </Space>
                </Space>
            </Card>
        ),
    }), [filter, allTasks.length, pendingTasksCount, overdueTasksCount, interviewTasksCount]);

    return (
        <div className="-mt-4">
            <ReusableTable<Task>
                config={config}
                transformResponse={transformResponse}
                key={`${filter}-${refreshKey}`}
            />

            {/* Task Details Drawer */}
            <DrawerComponent
                title={
                    <Space>
                        <ProfileCard
                            size={40}
                            name={selectedTask?.subject || 'Task Details'}
                            avatarUrl={null}
                            role="Task Information"
                        />
                    </Space>
                }
                visible={openDrawer}
                handleCancel={() => {
                    setOpenDrawer(false);
                    setSelectedTask(null);
                    // Refresh tasks when drawer closes
                    handleRefresh();
                }}
                isView={true}
                loading={false}
                submitForm={() => { }}
                rest={{
                    width: '90%',
                    style: {
                        background: '#eef1f9',
                        padding: '0px !important',
                    },
                    bodyStyle: { padding: 0 },
                    headerStyle: { background: '#FFFFFF', padding: '8px 10px' },
                }}
            >
                {selectedTask && <TasksDetails task={selectedTask} />}
            </DrawerComponent>
        </div>
    );
};

export default BDETasks;
